import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import Navbar from "../components/Navbar";
import ProviderTabs from "../components/ProviderTabs";
import ProviderLoadingScreen from "../components/ProviderLoadingScreen";
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ProviderBookings() {
  const { t, i18n } = useTranslation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/provider/bookings`, { withCredentials: true });
      setBookings(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/provider/bookings/${bookingId}`, { status: newStatus }, { withCredentials: true });
      fetchBookings();
    } catch (err) {
      // Antes: alert() nativo bloqueante. Toast consistente con el resto del panel.
      setToast(t('providerBookings.statusUpdateError'));
      setTimeout(() => setToast(null), 3500);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
      rejected: 'bg-red-100 text-red-800'
    };
    const translated = {
      pending: t('providerBookings.statusPending'),
      confirmed: t('providerBookings.statusConfirmed'),
      completed: t('providerBookings.statusCompleted'),
      cancelled: t('providerBookings.statusCancelled'),
      rejected: t('providerBookings.statusRejected')
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[status] || 'bg-gray-100'}`}>
        {translated[status] || status}
      </span>
    );
  };

  const locale = i18n.language?.startsWith('en') ? 'en-US' : 'es-DO';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <ProviderTabs />

      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-2xl shadow-lg text-white font-medium text-sm bg-red-600">
          {toast}
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-8">
        <h1 className="text-4xl font-heading font-bold mb-8">{t('providerBookings.title')}</h1>

        {loading ? (
          <ProviderLoadingScreen chrome={false} className="py-20" message={t('providerBookings.loading', 'Cargando tus reservas...')} />
        ) : bookings.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 shadow-sm text-center">
            <p className="text-gray-500">{t('providerBookings.noBookings')}</p>
          </div>
        ) : (
          <div className="grid gap-6">
            {bookings.map(booking => (
              <div key={booking.booking_id} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-6 items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-bold text-lg text-[#111827]">{booking.asset?.name}</span>
                    <span className="text-sm text-gray-500">{booking.booking_code}</span>
                    {getStatusBadge(booking.status)}
                  </div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm text-gray-600">
                    <div><span className="font-medium">{t('providerBookings.client')}:</span> {booking.client_name}</div>
                    <div><span className="font-medium">{t('providerBookings.date')}:</span> {new Date(booking.start_datetime).toLocaleString(locale)}</div>
                    <div><span className="font-medium">{t('providerBookings.duration')}:</span> {booking.end_hours} {t('providerBookings.hoursLabel')}</div>
                    <div><span className="font-medium">{t('providerBookings.total')}:</span> <span className="text-[#134168] font-bold">{formatDOP(booking.total_price)}</span></div>
                    {booking.delivery_requested && (
                      <div className="col-span-2 mt-1 p-2 bg-blue-50 border border-blue-100 rounded-lg flex gap-2 items-start">
                        <span className="font-bold text-blue-800 whitespace-nowrap">🚚 Delivery:</span>
                        <span className="text-blue-700">{booking.delivery_address}</span>
                      </div>
                    )}
                    {booking.refund && (
                      <div className={`col-span-2 mt-1 p-2 rounded-lg text-sm ${booking.refund.status === 'processed' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}>
                        <span className="font-bold">{t('providerBookings.refundLabel', 'Reembolso')}:</span>{' '}
                        {formatDOP(booking.refund.amount)} — {booking.refund.status === 'processed' ? t('providerBookings.refundProcessed', 'procesado') : t('providerBookings.refundPending', 'pendiente')}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {booking.status === 'pending' || booking.status === 'confirmed' ? (
                    <>
                      {booking.status === 'pending' && (
                        <button
                          onClick={() => handleStatusChange(booking.booking_id, 'confirmed')}
                          className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-full font-bold hover:bg-green-600 transition"
                        >
                          <Check className="w-4 h-4" /> {t('providerBookings.confirm')}
                        </button>
                      )}
                      {(booking.status === 'pending' || booking.status === 'confirmed') && (
                        <button
                          onClick={() => handleStatusChange(booking.booking_id, booking.status === 'pending' ? 'rejected' : 'cancelled')}
                          className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-full font-bold hover:bg-red-50 transition"
                        >
                          <X className="w-4 h-4" /> {booking.status === 'pending' ? t('providerBookings.reject') : t('providerBookings.statusCancelled', 'Cancelar')}
                        </button>
                      )}
                      {booking.status === 'confirmed' && (
                        <button
                          onClick={() => handleStatusChange(booking.booking_id, 'completed')}
                          className="flex items-center gap-2 px-4 py-2 bg-[#111827] text-white rounded-full font-bold hover:bg-black transition"
                        >
                          {t('providerBookings.markCompleted')}
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-gray-400 italic">{t('providerBookings.noActions')}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
